import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { registroActividadesAPI, authAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
    ArrowLeft,
    History,
    Search,
    Filter,
    RefreshCw,
    Undo2,
    Trash2,
    ChevronDown,
    ChevronUp,
    User,
    Calendar,
    FileText,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Eye,
    Flame,
} from 'lucide-react';

// Mapeo de acciones a colores y etiquetas
const ACCION_CONFIG = {
    crear: { color: 'bg-green-500', label: 'Crear', icon: CheckCircle },
    editar: { color: 'bg-blue-500', label: 'Editar', icon: FileText },
    eliminar: { color: 'bg-red-500', label: 'Eliminar', icon: XCircle },
    asignar: { color: 'bg-purple-500', label: 'Asignar', icon: User },
    desasignar: { color: 'bg-orange-500', label: 'Desasignar', icon: User },
    revertir: { color: 'bg-yellow-500', label: 'Revertir', icon: Undo2 },
};

// Mapeo de entidades de bomberos
const ENTIDAD_LABELS = {
    personal: 'Personal',
    capacitacion: 'Capacitación',
    epp: 'EPP / Material Menor',
    material_mayor: 'Vehículos / Material Mayor',
    acta: 'Acta',
    alerta: 'Alerta',
    compania: 'Compañía',
    rol: 'Rol',
};

export default function RegistroActividadesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [registros, setRegistros] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    
    // Filtros - módulo fijo en 'bomberos'
    const [filtros, setFiltros] = useState({
        entidad: '',
        usuario_id: '',
        fecha_desde: '',
        fecha_hasta: '',
        limit: 100,
    });
    const [showFilters, setShowFilters] = useState(false);
    
    // Dialogs
    const [showDetalleDialog, setShowDetalleDialog] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const [showRevertirDialog, setShowRevertirDialog] = useState(false);
    const [showEliminarDialog, setShowEliminarDialog] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchRegistros = useCallback(async () => {
        try {
            setLoading(true);
            const params = { modulo: 'bomberos' }; // Siempre filtrar por módulo bomberos
            if (filtros.entidad) params.entidad = filtros.entidad;
            if (filtros.usuario_id) params.usuario_id = filtros.usuario_id;
            if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
            if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
            params.limit = filtros.limit;
            
            const response = await registroActividadesAPI.getAll(params);
            setRegistros(response.data);
        } catch (error) {
            console.error('Error fetching registros:', error);
            toast.error('Error al cargar los registros de actividad');
        } finally {
            setLoading(false);
        }
    }, [filtros]);

    const fetchUsuarios = async () => {
        try {
            const response = await authAPI.getUsuarios();
            setUsuarios(response.data);
        } catch (error) {
            console.error('Error fetching usuarios:', error);
        }
    };

    useEffect(() => {
        fetchRegistros();
        fetchUsuarios();
    }, [fetchRegistros]);

    const handleRevertir = async () => {
        if (!selectedRegistro) return;
        
        try {
            setActionLoading(true);
            await registroActividadesAPI.revertir(selectedRegistro.id);
            toast.success('Actividad revertida correctamente');
            setShowRevertirDialog(false);
            setSelectedRegistro(null);
            fetchRegistros();
        } catch (error) {
            console.error('Error reverting:', error);
            toast.error(error.response?.data?.detail || 'Error al revertir la actividad');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEliminar = async () => {
        if (!selectedRegistro) return;
        
        try {
            setActionLoading(true);
            await registroActividadesAPI.eliminar(selectedRegistro.id);
            toast.success('Registro eliminado correctamente');
            setShowEliminarDialog(false);
            setSelectedRegistro(null);
            fetchRegistros();
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error(error.response?.data?.detail || 'Error al eliminar el registro');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getAccionBadge = (accion) => {
        const config = ACCION_CONFIG[accion] || { color: 'bg-gray-500', label: accion };
        return (
            <Badge className={`${config.color} text-white`}>
                {config.label}
            </Badge>
        );
    };

    const clearFilters = () => {
        setFiltros({
            entidad: '',
            usuario_id: '',
            fecha_desde: '',
            fecha_hasta: '',
            limit: 100,
        });
    };

    const renderDetalleDatos = (datos, titulo) => {
        if (!datos || Object.keys(datos).length === 0) {
            return (
                <div className="text-sm text-muted-foreground italic">
                    Sin datos
                </div>
            );
        }
        
        return (
            <div className="space-y-2">
                <h4 className="font-medium text-sm">{titulo}</h4>
                <div className="bg-muted/50 rounded-lg p-3 max-h-60 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap break-words">
                        {JSON.stringify(datos, null, 2)}
                    </pre>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-card border-b border-border shadow-sm">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => navigate(-1)}
                                data-testid="back-button"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div className="flex items-center gap-3">
                                <div className="bg-red-500/10 p-2 rounded-lg">
                                    <Flame className="h-6 w-6 text-red-500" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold">Registro de Actividades - Bomberos</h1>
                                    <p className="text-sm text-muted-foreground">
                                        Auditoría y seguimiento de cambios en el módulo de bomberos
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowFilters(!showFilters)}
                                data-testid="toggle-filters-btn"
                            >
                                <Filter className="h-4 w-4 mr-2" />
                                Filtros
                                {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={fetchRegistros}
                                disabled={loading}
                                data-testid="refresh-btn"
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                Actualizar
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 py-6">
                {/* Panel de Filtros */}
                {showFilters && (
                    <Card className="mb-6" data-testid="filters-panel">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Search className="h-5 w-5" />
                                Filtros de Búsqueda
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <div>
                                    <Label>Entidad</Label>
                                    <Select
                                        value={filtros.entidad}
                                        onValueChange={(value) => setFiltros({...filtros, entidad: value === 'all' ? '' : value})}
                                    >
                                        <SelectTrigger data-testid="filter-entidad">
                                            <SelectValue placeholder="Todas" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todas</SelectItem>
                                            <SelectItem value="personal">Personal</SelectItem>
                                            <SelectItem value="capacitacion">Capacitación</SelectItem>
                                            <SelectItem value="epp">EPP / Material Menor</SelectItem>
                                            <SelectItem value="material_mayor">Vehículos / Material Mayor</SelectItem>
                                            <SelectItem value="acta">Acta</SelectItem>
                                            <SelectItem value="compania">Compañía</SelectItem>
                                            <SelectItem value="rol">Rol</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label>Usuario</Label>
                                    <Select
                                        value={filtros.usuario_id}
                                        onValueChange={(value) => setFiltros({...filtros, usuario_id: value === 'all' ? '' : value})}
                                    >
                                        <SelectTrigger data-testid="filter-usuario">
                                            <SelectValue placeholder="Todos" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Todos</SelectItem>
                                            {usuarios.map((u) => (
                                                <SelectItem key={u.id} value={u.id}>
                                                    {u.nombre}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label>Desde</Label>
                                    <Input
                                        type="date"
                                        value={filtros.fecha_desde}
                                        onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
                                        data-testid="filter-fecha-desde"
                                    />
                                </div>

                                <div>
                                    <Label>Hasta</Label>
                                    <Input
                                        type="date"
                                        value={filtros.fecha_hasta}
                                        onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
                                        data-testid="filter-fecha-hasta"
                                    />
                                </div>

                                <div>
                                    <Label>Límite</Label>
                                    <Select
                                        value={filtros.limit.toString()}
                                        onValueChange={(value) => setFiltros({...filtros, limit: parseInt(value)})}
                                    >
                                        <SelectTrigger data-testid="filter-limit">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="50">50 registros</SelectItem>
                                            <SelectItem value="100">100 registros</SelectItem>
                                            <SelectItem value="200">200 registros</SelectItem>
                                            <SelectItem value="500">500 registros</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            <div className="flex justify-end mt-4 gap-2">
                                <Button variant="outline" onClick={clearFilters}>
                                    Limpiar filtros
                                </Button>
                                <Button onClick={fetchRegistros} data-testid="apply-filters-btn">
                                    <Search className="h-4 w-4 mr-2" />
                                    Buscar
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tabla de Registros */}
                <Card data-testid="registros-table-card">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Historial de Actividades</CardTitle>
                                <CardDescription>
                                    {registros.length} registro(s) encontrado(s)
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-4">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-16 w-full" />
                                ))}
                            </div>
                        ) : registros.length === 0 ? (
                            <div className="text-center py-12">
                                <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                <h3 className="text-lg font-medium">No hay registros</h3>
                                <p className="text-muted-foreground">
                                    No se encontraron actividades con los filtros seleccionados
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[180px]">Fecha</TableHead>
                                            <TableHead className="w-[140px]">Entidad</TableHead>
                                            <TableHead className="w-[100px]">Acción</TableHead>
                                            <TableHead>Descripción</TableHead>
                                            <TableHead className="w-[150px]">Usuario</TableHead>
                                            <TableHead className="w-[100px]">Estado</TableHead>
                                            <TableHead className="w-[120px] text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {registros.map((registro) => (
                                            <TableRow 
                                                key={registro.id}
                                                className={registro.revertido ? 'opacity-60 bg-muted/30' : ''}
                                                data-testid={`registro-row-${registro.id}`}
                                            >
                                                <TableCell className="font-mono text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                                        {formatDate(registro.fecha)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {ENTIDAD_LABELS[registro.entidad] || registro.entidad}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {getAccionBadge(registro.accion)}
                                                </TableCell>
                                                <TableCell className="max-w-[300px] truncate">
                                                    {registro.descripcion}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <User className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm">{registro.usuario_nombre}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {registro.revertido ? (
                                                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                                                            Revertido
                                                        </Badge>
                                                    ) : registro.revertible ? (
                                                        <Badge variant="outline" className="text-green-600 border-green-600">
                                                            Revertible
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline" className="text-gray-500">
                                                            No revertible
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => {
                                                                setSelectedRegistro(registro);
                                                                setShowDetalleDialog(true);
                                                            }}
                                                            title="Ver detalles"
                                                            data-testid={`view-detail-btn-${registro.id}`}
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </Button>
                                                        {registro.revertible && !registro.revertido && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => {
                                                                    setSelectedRegistro(registro);
                                                                    setShowRevertirDialog(true);
                                                                }}
                                                                title="Revertir"
                                                                className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                                                data-testid={`revert-btn-${registro.id}`}
                                                            >
                                                                <Undo2 className="h-4 w-4" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => {
                                                                setSelectedRegistro(registro);
                                                                setShowEliminarDialog(true);
                                                            }}
                                                            title="Eliminar registro"
                                                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                            data-testid={`delete-btn-${registro.id}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </main>

            {/* Dialog: Ver Detalle */}
            <Dialog open={showDetalleDialog} onOpenChange={setShowDetalleDialog}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="h-5 w-5" />
                            Detalle del Registro
                        </DialogTitle>
                        <DialogDescription>
                            Información completa de la actividad registrada
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedRegistro && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Fecha</Label>
                                    <p className="font-medium">{formatDate(selectedRegistro.fecha)}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Usuario</Label>
                                    <p className="font-medium">{selectedRegistro.usuario_nombre}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Entidad</Label>
                                    <p className="font-medium">
                                        {ENTIDAD_LABELS[selectedRegistro.entidad] || selectedRegistro.entidad}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Acción</Label>
                                    {getAccionBadge(selectedRegistro.accion)}
                                </div>
                                <div className="col-span-2">
                                    <Label className="text-muted-foreground">ID Entidad</Label>
                                    <p className="font-mono text-sm">{selectedRegistro.entidad_id}</p>
                                </div>
                            </div>

                            <div>
                                <Label className="text-muted-foreground">Descripción</Label>
                                <p className="font-medium mt-1">{selectedRegistro.descripcion}</p>
                            </div>

                            {selectedRegistro.revertido && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 text-yellow-800">
                                        <AlertTriangle className="h-5 w-5" />
                                        <span className="font-medium">Esta acción fue revertida</span>
                                    </div>
                                    <p className="text-sm text-yellow-700 mt-1">
                                        Por: {selectedRegistro.revertido_por} el {formatDate(selectedRegistro.revertido_fecha)}
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {renderDetalleDatos(selectedRegistro.datos_anteriores, 'Datos Anteriores')}
                                {renderDetalleDatos(selectedRegistro.datos_nuevos, 'Datos Nuevos')}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetalleDialog(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* AlertDialog: Confirmar Revertir */}
            <AlertDialog open={showRevertirDialog} onOpenChange={setShowRevertirDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Undo2 className="h-5 w-5 text-yellow-500" />
                            Confirmar Reversión
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            ¿Está seguro de que desea revertir esta acción? Esta operación restaurará 
                            los datos al estado anterior.
                            {selectedRegistro && (
                                <div className="mt-4 p-3 bg-muted rounded-lg">
                                    <p className="font-medium">{selectedRegistro.descripcion}</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Realizado por {selectedRegistro.usuario_nombre} el {formatDate(selectedRegistro.fecha)}
                                    </p>
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={actionLoading}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRevertir}
                            disabled={actionLoading}
                            className="bg-yellow-500 hover:bg-yellow-600"
                            data-testid="confirm-revert-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Revirtiendo...
                                </>
                            ) : (
                                <>
                                    <Undo2 className="h-4 w-4 mr-2" />
                                    Revertir
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* AlertDialog: Confirmar Eliminar */}
            <AlertDialog open={showEliminarDialog} onOpenChange={setShowEliminarDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-red-500" />
                            Confirmar Eliminación
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            ¿Está seguro de que desea eliminar este registro de auditoría? 
                            Esta acción no se puede deshacer.
                            {selectedRegistro && (
                                <div className="mt-4 p-3 bg-muted rounded-lg">
                                    <p className="font-medium">{selectedRegistro.descripcion}</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Realizado por {selectedRegistro.usuario_nombre} el {formatDate(selectedRegistro.fecha)}
                                    </p>
                                </div>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={actionLoading}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleEliminar}
                            disabled={actionLoading}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-btn"
                        >
                            {actionLoading ? (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                    Eliminando...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Eliminar
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
